import tenantData from '@data';
import tenantTransformers from '@transformers';
import { getBayutKsaStaticDynamicFieldsResponse } from '../../tenant/bayut/data/listingCategories/ksa';
import {
  skipFieldsForField as bayutSkipFieldRules,
  showFieldsForPropertyType as bayutShowFieldRules,
} from '../../tenant/bayut/data/listing-form-data';
import { getCommonStaticDynamicFieldsResponse } from '../../tenant/common/data/listingCategories/common';
import { TENANT_KEY } from '../../utility/env';

const isBayut = TENANT_KEY === 'bayut';

const getStaticDynamicFieldsResponse = (arg) =>
  isBayut
    ? getBayutKsaStaticDynamicFieldsResponse(arg)
    : getCommonStaticDynamicFieldsResponse(arg);

export const getDynamicFormFieldsData = (arg = {}) => {
  const response = getStaticDynamicFieldsResponse(arg);
  if (response == null) return null;

  const { locale, user, cities, useSecondaryDisplayOrder, isDailyRental, propertyTypeId, categorySlug, listingPurposeKey } = arg;

  const showFieldRules = isBayut ? bayutShowFieldRules : undefined;
  // Slug-based field visibility: Oman, Qatar, Bahrain, Jordan (Total Floors / Floor Number /
  // Built-up Area rollout also relies on this). Other tenants keep their existing numeric
  // `skipFieldRules` path.
  const useSlugFieldVisibility = ['oman', 'qatar', 'bahrain', 'jordan'].includes(TENANT_KEY);
  const skipFieldRules = isBayut ? bayutSkipFieldRules : tenantData?.skipFieldsForField;

  return tenantTransformers.dynamicFormFieldMapper(response, locale, user, cities || [], {
    useSecondaryDisplayOrder: Boolean(useSecondaryDisplayOrder),
    isDailyRental: Boolean(isDailyRental),
    propertyTypeId: propertyTypeId != null && propertyTypeId !== '' ? Number(propertyTypeId) : null,
    skipFieldRules,
    showFieldRules,
    // Oman/Qatar/Bahrain/Jordan hide beds/baths/furnished/features (and gate total_floors/floor_number)
    // by stable category slug (the purpose-specific numeric external_id is unreliable). All other
    // tenants keep the numeric `skipFieldRules` path.
    categorySlug: useSlugFieldVisibility ? categorySlug : undefined,
    purposeSlug: useSlugFieldVisibility ? listingPurposeKey : undefined,
    injectClientLocationField: true,
  });
};

/** Normalize listing `price` Formik value (number or `{ value }`) for numeric comparisons. */
export function listingPriceToNumber(raw) {
  if (raw == null || raw === '') return NaN;
  const val = typeof raw === 'object' && raw !== null && 'value' in raw ? raw.value : raw;
  const n = Number(val);
  return Number.isFinite(n) ? n : NaN;
}

const ENGLISH_TITLE_DESC_FORM_KEYS = [
  'title',
  'description',
  'property_title_en',
  'property_description_en',
];
const ARABIC_TITLE_DESC_FORM_KEYS = [
  'title_l1',
  'description_l1',
  'property_title_ar',
  'property_description_ar',
];

const isTitleOrDescriptionFormKey = (key) => key.includes('title') || key.includes('description');

// Must be scoped to title/description keys: a bare `_ar` substring match also hits
// `built_up_area`, hiding it on the English locale of every LOCALE_SPECIFIC_TITLE_DESCRIPTION tenant.
export function isArabicTitleDescriptionFormKey(key) {
  return isTitleOrDescriptionFormKey(key) && (key.endsWith('_l1') || key.includes('_ar'));
}

export function isEnglishTitleDescriptionFormKey(key) {
  return (
    key === 'title' ||
    key === 'description' ||
    (key.includes('_en') && (key.includes('title') || key.includes('description')))
  );
}

export function getLocaleHiddenTitleDescriptionFormKeys(locale) {
  return locale === 'ar' ? ENGLISH_TITLE_DESC_FORM_KEYS : ARABIC_TITLE_DESC_FORM_KEYS;
}

/**
 * Title/description keys to omit from the form and validation.
 * GCC common tenants: show only the active UI locale's fields.
 * Bayut KSA: hide the opposite locale when auto-translate is enabled.
 */
export function getHiddenFieldKeysForAutoTranslate({
  isLocaleSpecificTitleDescription,
  isLocaleAwareGenerateContent,
  locale,
  generateTitle,
  generateDescription,
}) {
  if (isLocaleSpecificTitleDescription) {
    return getLocaleHiddenTitleDescriptionFormKeys(locale);
  }

  const hidden = [];
  if (generateTitle) {
    if (isLocaleAwareGenerateContent) {
      hidden.push(locale === 'ar' ? 'property_title_en' : 'property_title_ar');
    } else {
      hidden.push('property_title_ar');
    }
  }
  if (generateDescription) {
    if (isLocaleAwareGenerateContent) {
      hidden.push(locale === 'ar' ? 'property_description_en' : 'property_description_ar');
    } else {
      hidden.push('property_description_ar');
    }
  }
  return hidden;
}
